import React, { useState, useEffect } from 'react';
import { complaintService, staffService } from '../services/api';

const Complaints: React.FC = () => {
  const [complaints, setComplaints] = useState<any[]>([]);
  const [staff, setStaff] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [selectedStaffMap, setSelectedStaffMap] = useState<Record<string, string>>({});

  useEffect(() => {
    loadComplaints();
    loadStaff();
  }, []);

  const loadComplaints = async () => {
    try {
      const response = await complaintService.getAll();
      setComplaints(response.data.data.complaints);
    } catch (error) {
      console.error('Error loading complaints:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStaff = async () => {
    try {
      const response = await staffService.getAll();
      setStaff(response.data.data.staff);
    } catch (error) {
      console.error('Error loading staff:', error);
    }
  };

  const handleAssign = async (complaintId: string) => {
    const staffId = selectedStaffMap[complaintId];
    if (!staffId) {
      alert('Please select a staff member');
      return;
    }
    try {
      setAssigningId(complaintId);
      await complaintService.assign(complaintId, staffId);
      setAssigningId(null);
      const newMap = { ...selectedStaffMap };
      delete newMap[complaintId];
      setSelectedStaffMap(newMap);
      loadComplaints();
    } catch (error: any) {
      setAssigningId(null);
      alert(error.response?.data?.message || 'Error assigning complaint');
    }
  };

  const handleStaffSelect = (complaintId: string, staffId: string) => {
    setSelectedStaffMap({
      ...selectedStaffMap,
      [complaintId]: staffId,
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryBadgeClass = (category: string) => {
    switch (category?.toLowerCase()) {
      case 'cleaning':
        return 'bg-purple-100 text-purple-800';
      case 'security':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900">Complaints Management</h1>

      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Shopkeeper</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Shop/Office</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Assigned To</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {complaints.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    No complaints found
                  </td>
                </tr>
              ) : (
                complaints.map((complaint) => (
                  <tr
                    key={complaint._id}
                    className={`hover:bg-gray-50 transition-colors ${complaint.status === 'pending' ? 'bg-yellow-50' : ''
                      }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getCategoryBadgeClass(complaint.category)}`}>
                        {complaint.category?.toUpperCase() || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-md">{complaint.description || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        {complaint.shopkeeperId?.photo ? (
                          <img
                            src={complaint.shopkeeperId.photo}
                            alt={complaint.shopkeeperId.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-primary-500 text-white flex items-center justify-center font-semibold">
                            {complaint.shopkeeperId?.name?.charAt(0).toUpperCase() || '?'}
                          </div>
                        )}
                        <span className="text-sm text-gray-900">{complaint.shopkeeperId?.name || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-900 font-medium">
                          {complaint.shopId?.shopName || 'N/A'}
                        </span>
                        {complaint.shopId?.officeNo && (
                          <span className="text-xs text-gray-500">
                            Office: {complaint.shopId.officeNo}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {complaint.staffId ? (
                        <div className="flex flex-col gap-1">
                          <span className="text-sm text-gray-900">{complaint.staffId.name}</span>
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-semibold w-fit">
                            {complaint.staffId.staffType}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400">Unassigned</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(complaint.status)}`}>
                        {complaint.status === 'in_progress' ? 'In Progress' : complaint.status?.toUpperCase() || 'UNKNOWN'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {complaint.createdAt ? new Date(complaint.createdAt).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {complaint.status === 'pending' ? (
                        <div className="flex flex-col gap-2">
                          <select
                            value={selectedStaffMap[complaint._id] || ''}
                            onChange={(e) => handleStaffSelect(complaint._id, e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                            disabled={assigningId === complaint._id}
                          >
                            <option value="">Select Staff</option>
                            {staff.map((s) => (
                              <option key={s._id} value={s._id}>
                                {s.name} ({s.staffType})
                              </option>
                            ))}
                          </select>
                          {selectedStaffMap[complaint._id] && (
                            <button
                              onClick={() => handleAssign(complaint._id)}
                              className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                              disabled={assigningId === complaint._id}
                            >
                              {assigningId === complaint._id ? 'Assigning...' : 'Assign'}
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Complaints;
